import React, { useState, useEffect, useMemo } from 'react';
import { httpApi as api } from './api';
import CustomAppBar from './CustomAppBar';
import {
  Container, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Typography
} from '@mui/material';
import { useUser } from './UserContext';
import { useTheme } from '@mui/material/styles';

const Positions = () => {
  const theme = useTheme();
  const { user } = useUser();
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sortConfig, setSortConfig] = useState({
    key: 'exchange_timestamp',
    direction: 'desc',
  });

  useEffect(() => {
    const fetchPositions = async () => {
      setLoading(true);
      try {
        const response = await api.get(`/all_positions`);
        const mappedPositions = response.data.map(position => ({
          ...position,
          last_price: position.price,
          average_price: position.average_price || position.price,
          pnl: position.overall_trade_profit,
        }));
        setPositions(mappedPositions);
      } catch (error) {
        console.error('Error fetching positions:', error);
        setError('Failed to fetch positions');
      } finally {
        setLoading(false);
      }
    };

    fetchPositions();
  }, []);
  

  const formatDate = (dateString) => {
    // Parse the original GMT date string directly
    const date = new Date(dateString);
    
    // Extract components WITHOUT timezone conversion
    const day = String(date.getUTCDate()).padStart(2, '0');
    const month = date.toLocaleString('default', { month: 'short' });
    const year = date.getUTCFullYear();
    const hours = String(date.getUTCHours()).padStart(2, '0');
    const minutes = String(date.getUTCMinutes()).padStart(2, '0');
  
    return `${day}-${month}-${year} ${hours}:${minutes}`;
  };

  const handleSort = () => {
    setSortConfig(prev => ({
      ...prev,
      direction: prev.direction === 'desc' ? 'asc' : 'desc',
    }));
  };

  const sortedPositions = useMemo(() => {
    return [...positions].sort((a, b) => {
      const dateA = new Date(a.exchange_timestamp).getTime();
      const dateB = new Date(b.exchange_timestamp).getTime();
      return sortConfig.direction === 'desc' ? dateB - dateA : dateA - dateB;
    });
  }, [positions, sortConfig.direction]);

  const getPnlStyle = (pnl) => ({
    color: pnl >= 0 ? theme.palette.success.main : theme.palette.error.main,
    fontWeight: 'bold'
  });

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2,
    }).format(value);
  };

  const totalPnL = useMemo(
    () => positions.reduce((acc, position) => acc + Number(position.pnl || 0), 0),
    [positions]
  );
  return (
    <>
      <CustomAppBar />
      <Container maxWidth="md" sx={{ 
        p: 0, 
        pl: { xs: 0, sm: 2 }, 
        maxWidth: '100% !important', 
        overflowX: 'hidden' 
      }}>
        <Typography variant="h4" sx={{ 
          fontWeight: 'bold', 
          p: 2,
          backgroundColor: theme.palette.background.paper,
          position: 'sticky',
          top: 0,
          zIndex: 1
        }}>
          Positions Data
        </Typography>

        {loading ? (
          <div style={{ textAlign: 'center', margin: '16px 0' }}>Loading...</div>
        ) : error ? (
          <div style={{ color: 'red', textAlign: 'center', margin: '16px 0' }}>{error}</div>
        ) : (
          <Paper sx={{ 
            mx: { xs: 0, sm: 2 }, 
            overflowX: 'auto',
            boxShadow: theme.shadows[3],
            borderRadius: '8px'
          }}>
            <TableContainer>
              <Table sx={{ 
                minWidth: 800,
                '& .MuiTableCell-root': {
                  py: 1.5,
                  fontSize: { xs: '0.75rem', sm: '0.875rem' }
                }
              }}>
                <TableHead sx={{ backgroundColor: theme.palette.grey[100] }}>
                  <TableRow>
                    <TableCell 
                      sx={{ 
                        fontWeight: 'bold', 
                        minWidth: 140,
                        cursor: 'pointer',
                        '&:hover': { backgroundColor: theme.palette.action.hover }
                      }}
                      onClick={handleSort}
                    >
                      Order Time {sortConfig.direction === 'desc' ? '▼' : '▲'}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Symbol</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', display: { xs: 'none', sm: 'table-cell' } }} align="right">Type</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="right">Qty</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', display: { xs: 'none', sm: 'table-cell' } }} align="right">Avg Price</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', display: { xs: 'none', md: 'table-cell' } }} align="right">Buy Cost</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', display: { xs: 'none', md: 'table-cell' } }} align="right">Sell Cost</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="right">P&L</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', display: { xs: 'none', md: 'table-cell' } }} align="right">Funds</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', display: { xs: 'none', lg: 'table-cell' } }} align="right">Tag</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedPositions.map((position, index) => (
                    <TableRow key={index} hover sx={{ '&:nth-of-type(odd)': { backgroundColor: theme.palette.action.hover } }}>
                      <TableCell sx={{ minWidth: 140 }}>
                        {formatDate(position.exchange_timestamp)}
                      </TableCell>
                      <TableCell>{position.tradingsymbol}</TableCell>
                      <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                        {position.transaction_type}
                      </TableCell>
                      <TableCell align="right">{position.quantity}</TableCell>
                      <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                        {position.price}
                      </TableCell>
                      <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        {position.buy_cost}
                      </TableCell>
                      <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        {position.sell_cost}
                      </TableCell>
                      <TableCell align="right" sx={getPnlStyle(position.overall_trade_profit)}>
                        {formatCurrency(position.overall_trade_profit)}
                      </TableCell>
                      <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        {position.available_margin}
                      </TableCell>
                      <TableCell align="right" sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                        {position.tag}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow sx={{ backgroundColor: theme.palette.grey[100] }}>
                    <TableCell colSpan={7} align="right" sx={{ fontWeight: 'bold' }}>
                      Total P&L:
                    </TableCell>
                    <TableCell align="right" sx={getPnlStyle(totalPnL)}>
                      {formatCurrency(totalPnL)}
                    </TableCell>
                    <TableCell colSpan={2} />
                  </TableRow>
                  {sortedPositions.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={10} align="center">
                        No positions found.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}
      </Container>
    </>
  );
};

export default Positions;